import React from "react";
import { motion } from "framer-motion";
import { Trophy, ArrowUpRight } from "lucide-react";
import { ASSETS } from "@/data/siteContent";

export default function TennisShowcase({ onBookConsultation }) {
  return (
    <section
      id="tennis"
      data-testid="tennis-showcase"
      className="relative bg-white py-24 md:py-32 overflow-hidden"
    >
      <div className="container-prose">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left copy column */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 lg:sticky lg:top-32"
          >
            <p className="eyebrow mb-5">— Athletic Scholarships</p>
            <h2 className="font-serif text-navy text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-tight">
              Built for athletes who <span className="italic">play to win.</span>
            </h2>
            <p className="mt-6 text-navy-deeper/75 text-base md:text-lg leading-relaxed font-sans max-w-md">
              From UTR rankings to Division-I scholarships — across tennis, basketball and beyond,
              we decode what US college coaches really look for, then build the player profile
              that gets the call back.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-5 max-w-md">
              {[
                ["Athlete profiles built", "120+"],
                ["NCAA placements", "35+"],
                ["Avg. UTR ↑ in 12 mo.", "+1.8"],
                ["Coach-emails crafted", "1,400+"],
              ].map(([label, value]) => (
                <div key={label} className="border-t border-navy/10 pt-3">
                  <div className="font-serif text-2xl md:text-3xl text-navy leading-none">
                    {value}
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-navy/55 mt-2">
                    {label}
                  </div>
                </div>
              ))}
            </div>

            <button
              data-testid="tennis-book-consultation"
              onClick={onBookConsultation}
              className="mt-10 group inline-flex items-center gap-2 bg-navy text-white px-7 py-4 text-xs md:text-sm tracking-[0.2em] uppercase font-semibold rounded-sm hover:bg-navy-dark transition-all"
            >
              <Trophy size={16} className="text-gold" />
              Start Your Recruit Profile
              <ArrowUpRight
                size={16}
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
              />
            </button>
          </motion.div>

          {/* Right photo collage — 3 editorial tiles */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-6 grid-rows-6 gap-3 md:gap-4 h-[520px] md:h-[600px] lg:h-[680px]">
              <TennisTile
                src={ASSETS.tennis.hero}
                alt="College tennis player mid-rally"
                className="col-span-4 row-span-6"
                tag="Tennis · Match Day"
                delay={0}
              />
              <TennisTile
                src={ASSETS.tennis.serve}
                alt="College tennis serve"
                className="col-span-2 row-span-3"
                tag="Tennis · The Serve"
                delay={0.1}
              />
              <TennisTile
                src={ASSETS.tennis.basketball}
                alt="NCAA basketball arena"
                className="col-span-2 row-span-3"
                tag="Basketball · NCAA"
                delay={0.2}
              />
            </div>

            <p className="mt-5 text-xs uppercase tracking-[0.3em] text-navy/45 text-right font-medium">
              Guided by Garvita Dutta · Pro Tennis Player · Georgetown
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function TennisTile({ src, alt, className = "", tag, delay = 0 }) {
  return (
    <motion.figure
      initial={{ opacity: 0, scale: 0.96, y: 16 }}
      whileInView={{ opacity: 1, scale: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative overflow-hidden bg-navy/5 rounded-sm ${className}`}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.07]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/55 via-transparent to-transparent" />
      <figcaption className="absolute bottom-2 left-3 right-3 flex items-center justify-between">
        <span className="text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-white/95 font-semibold drop-shadow">
          {tag}
        </span>
        <span className="h-1.5 w-1.5 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
      </figcaption>
    </motion.figure>
  );
}
